import { useCallback, useEffect, useState } from "react";

import { ApiFailure, api } from "../lib/api";
import { Badge, Button, Card, ErrorNote, Skeleton, inputClass } from "../components/ui";

interface SettingsPayload {
  values: Record<string, unknown>;
  descriptions: Record<string, string>;
  dangerous: string[];
}

interface DiscordRole {
  id: string;
  name: string;
  color: number;
  /** False means naming this role in a message will not notify anyone. */
  mentionable: boolean;
}

interface GuildRoles {
  guild: { id: string; name: string };
  roles: DiscordRole[];
}

/**
 * Operator settings.
 *
 * The approver roles are the important control on this page, and the reason it
 * exists: the Python bot let anyone approve when no roles were configured, and
 * said nothing. Here that state is visible, loud, and has to be chosen.
 */
export function Settings() {
  const [data, setData] = useState<SettingsPayload | null>(null);
  const [guilds, setGuilds] = useState<GuildRoles[]>([]);
  const [canMentionAnyRole, setCanMentionAnyRole] = useState(true);
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [settings, roles] = await Promise.all([
        api.get<SettingsPayload>("/admin/settings"),
        api
          .get<{ guilds: GuildRoles[]; canMentionAnyRole: boolean }>("/admin/discord/roles")
          .catch(() => ({ guilds: [], canMentionAnyRole: true })),
      ]);
      setData(settings);
      setDraft(settings.values);
      setGuilds(roles.guilds);
      setCanMentionAnyRole(roles.canMentionAnyRole);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiFailure ? err.error.message : "Could not load settings.");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (!data) {
    return (
      <div className="grid gap-4">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  const approverRoles = (draft["approverRoleIds"] as string[]) ?? [];
  const notifyRoles = (draft["notifyRoleIds"] as string[]) ?? [];
  const ungated = Boolean(draft["allowUngatedApproval"]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(data.values);

  /**
   * Chosen roles that will be named in the ping but notify nobody.
   *
   * Only meaningful when the bot lacks Mention Everyone — with that permission
   * it can ping any role regardless of the role's own setting.
   */
  const silentRoles = canMentionAnyRole
    ? []
    : guilds
        .flatMap((g) => g.roles)
        .filter((r) => notifyRoles.includes(r.id) && !r.mentionable);

  // The exact state the rewrite exists to make visible.
  const gateOpen = approverRoles.length === 0 && ungated;
  const adminOnly = approverRoles.length === 0 && !ungated;

  function set(key: string, value: unknown) {
    setDraft((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await api.patch("/admin/settings", draft);
      await load();
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiFailure ? err.error.message : "Could not save.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="grid gap-6">
      <header>
        <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Settings</h1>
        <p className="mt-1 text-[13px] text-[var(--muted)]">
          Changes take effect on the next interaction. No deploy needed.
        </p>
      </header>

      {error && <ErrorNote>{error}</ErrorNote>}

      {gateOpen && (
        <Card className="border-[var(--danger)]/40 bg-[var(--danger)]/8">
          <div className="text-[15px] font-semibold text-[var(--danger)]">
            The approval step is switched off
          </div>
          <p className="mt-1 text-[13px] leading-relaxed text-[var(--muted)]">
            No approver roles are set and ungated approval is on, so anyone can approve — including the
            chatter who submitted. This is a logbook, not an approval step. Pick at least one approver role.
          </p>
        </Card>
      )}

      {adminOnly && (
        <Card className="border-[var(--warn)]/40 bg-[var(--warn)]/8">
          <div className="text-[15px] font-semibold text-[var(--warn)]">
            Only server administrators can approve
          </div>
          <p className="mt-1 text-[13px] leading-relaxed text-[var(--muted)]">
            No approver roles are configured. Nothing is broken and nobody is locked out, but your TLs
            cannot decide until you pick their role below.
          </p>
        </Card>
      )}

      <Card>
        <h2 className="text-[15px] font-semibold">Who can approve</h2>
        <p className="mt-1 text-[13px] text-[var(--muted)]">{data.descriptions["approverRoleIds"]}</p>

        <RolePicker
          guilds={guilds}
          selected={approverRoles}
          onToggle={(next) => set("approverRoleIds", next)}
        />

        <div className="mt-5 grid gap-3 border-t border-[var(--border)] pt-4">
          <Toggle
            label="Server administrators can always decide"
            hint={data.descriptions["guildAdminsMayDecide"] ?? ""}
            checked={Boolean(draft["guildAdminsMayDecide"])}
            onChange={(v) => set("guildAdminsMayDecide", v)}
          />
          <Toggle
            label="Allow self-approval"
            hint={data.descriptions["allowSelfDecide"] ?? ""}
            checked={Boolean(draft["allowSelfDecide"])}
            onChange={(v) => set("allowSelfDecide", v)}
            dangerous
          />
          <Toggle
            label="Allow anyone to approve when no roles are set"
            hint={data.descriptions["allowUngatedApproval"] ?? ""}
            checked={ungated}
            onChange={(v) => set("allowUngatedApproval", v)}
            dangerous
          />
        </div>
      </Card>

      <Card>
        <h2 className="text-[15px] font-semibold">Who gets pinged</h2>
        <p className="mt-1 text-[13px] text-[var(--muted)]">{data.descriptions["notifyRoleIds"]}</p>

        <RolePicker
          guilds={guilds}
          selected={notifyRoles}
          onToggle={(next) => set("notifyRoleIds", next)}
          warnUnmentionable={!canMentionAnyRole}
        />

        {silentRoles.length > 0 && (
          <div className="mt-4 rounded-[10px] bg-[var(--warn)]/8 px-3.5 py-3">
            <div className="text-[13px] font-medium text-[var(--warn)]">
              {silentRoles.length === 1
                ? `${silentRoles[0]?.name} will be named but not notified`
                : `${silentRoles.length} of these roles will be named but not notified`}
            </div>
            <p className="mt-1 text-[12px] leading-relaxed text-[var(--muted)]">
              Discord only delivers a role ping if the role allows it. In Discord, open{" "}
              <strong className="text-[var(--foreground)]">Server Settings → Roles</strong>, pick{" "}
              {silentRoles.map((r) => r.name).join(", ")}, and turn on{" "}
              <strong className="text-[var(--foreground)]">Allow anyone to @mention this role</strong>.
              The message will still be posted either way — it just will not light anyone up.
            </p>
          </div>
        )}

        {notifyRoles.length === 0 && (
          <p className="mt-4 text-[12px] leading-relaxed text-[var(--faint)]">
            Nobody is pinged. The review card is still posted in the model&apos;s channel; it just
            arrives quietly.
          </p>
        )}
      </Card>

      <Card>
        <h2 className="text-[15px] font-semibold">Submissions</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <NumberField
            label="Duplicate window (minutes)"
            hint={data.descriptions["duplicateWindowMinutes"] ?? ""}
            value={Number(draft["duplicateWindowMinutes"] ?? 120)}
            onChange={(v) => set("duplicateWindowMinutes", v)}
          />
          <NumberField
            label="Shortest message"
            hint={data.descriptions["minMessageLength"] ?? ""}
            value={Number(draft["minMessageLength"] ?? 8)}
            onChange={(v) => set("minMessageLength", v)}
          />
          <NumberField
            label="Longest message"
            hint={data.descriptions["maxMessageLength"] ?? ""}
            value={Number(draft["maxMessageLength"] ?? 900)}
            onChange={(v) => set("maxMessageLength", v)}
          />
        </div>
        <div className="mt-4 grid gap-3 border-t border-[var(--border)] pt-4">
          <Toggle
            label="DM the chatter when their proposal is decided"
            hint={data.descriptions["notifyAuthorOnDecision"] ?? ""}
            checked={Boolean(draft["notifyAuthorOnDecision"])}
            onChange={(v) => set("notifyAuthorOnDecision", v)}
          />
          <Toggle
            label="Ping them in the channel if their DMs are closed"
            hint={data.descriptions["fallbackToChannelPing"] ?? ""}
            checked={Boolean(draft["fallbackToChannelPing"])}
            onChange={(v) => set("fallbackToChannelPing", v)}
          />
        </div>
      </Card>

      <div className="sticky bottom-0 flex items-center gap-3 border-t border-[var(--border)] bg-[var(--background)] py-4">
        <Button variant="primary" onClick={() => void save()} disabled={saving || !dirty}>
          {saving ? "Saving…" : "Save changes"}
        </Button>
        {dirty && <span className="text-[13px] text-[var(--warn)]">Unsaved changes</span>}
        {saved && !dirty && <span className="text-[13px] text-[var(--ok)]">Saved</span>}
      </div>
    </div>
  );
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
  dangerous,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  dangerous?: boolean;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3">
      <input
        type="checkbox"
        className="mt-1 accent-[var(--brand)]"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="min-w-0">
        <span className="flex flex-wrap items-center gap-2 text-[13px] font-medium">
          {label}
          {dangerous && checked && <Badge tone="warn">weakens the gate</Badge>}
        </span>
        <span className="mt-0.5 block text-[12px] leading-snug text-[var(--faint)]">{hint}</span>
      </span>
    </label>
  );
}

function NumberField({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[13px] font-medium">{label}</span>
      <input
        className={`${inputClass} tabular`}
        type="number"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      <span className="mt-1.5 block text-[12px] leading-snug text-[var(--faint)]">{hint}</span>
    </label>
  );
}

/**
 * A Discord role picker.
 *
 * Shared by the approver list and the notification list — the same shape of
 * choice, and duplicating it would mean the day one gains a feature the other
 * quietly does not.
 */
function RolePicker({
  guilds,
  selected,
  onToggle,
  warnUnmentionable = false,
}: {
  guilds: GuildRoles[];
  selected: string[];
  onToggle: (next: string[]) => void;
  /** Mark roles that cannot be @mentioned. Only relevant for the ping list. */
  warnUnmentionable?: boolean;
}) {
  if (guilds.length === 0) {
    return (
      <p className="mt-3 text-[13px] text-[var(--faint)]">
        Roles load once the bot is connected to Discord.
      </p>
    );
  }

  return (
    <>
      {guilds.map((g) => (
        <div key={g.guild.id} className="mt-4">
          <div className="mb-2 text-[12px] font-medium uppercase tracking-[0.08em] text-[var(--faint)]">
            {g.guild.name}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {g.roles.map((r) => {
              const on = selected.includes(r.id);
              const silent = warnUnmentionable && on && !r.mentionable;
              return (
                <button
                  key={r.id}
                  onClick={() =>
                    onToggle(on ? selected.filter((x) => x !== r.id) : [...selected, r.id])
                  }
                  className={
                    "inline-flex items-center gap-1.5 rounded-[6px] border px-2.5 py-1 text-[12px] transition-colors duration-150 " +
                    (silent
                      ? "border-[var(--warn)]/45 bg-[var(--warn)]/10 text-[var(--warn)]"
                      : on
                        ? "border-[var(--brand)]/40 bg-[var(--brand-soft)] text-[var(--brand)]"
                        : "border-[var(--border)] bg-[var(--surface-2)] text-[var(--muted)] hover:border-[var(--border-strong)]")
                  }
                  title={silent ? "This role does not allow @mentions, so the ping will not notify anyone." : undefined}
                >
                  {r.name}
                  {/* A shape as well as a colour: the warning must not be
                      carried by hue alone. */}
                  {silent && <span aria-hidden="true">!</span>}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </>
  );
}
